import { forEach, isEmpty, map, some, toString } from 'lodash/fp';
import { Fragment, KeyboardEvent, useEffect, useState } from 'react';
import { Hidden } from '@ahmdigital/ui';
import { useFormikContext } from 'formik';
import { useRouter } from 'next/router';

import { DesktopSubmitButton, FormWrapper, MobileSubmitButton, StyledForm } from '../../styles';
import { InputSeparator } from '../step-input/styles';
import { SESSION_STORAGE_KEYS } from '../../constants';
import {
  SetIsWidgetActive,
  StepInputAppearance,
  SubmitButtonConfig,
  ToggleModal,
  WidgetButtonSpacing,
  WidgetData,
} from '../../types';
import browserUtil from '../../../../utils/browser';
import getFormValuesFromUrl from '../../utils/get-form-values-from-url';
import getShouldHideSeparator from '../../utils/get-should-hide-separator';
import rehydrateForm from '../../utils/rehydrate-form';
import StepInput, { StepInputProps } from '../step-input';
import uncapFpIterator from '../../../../../utils/uncap-fp-iterator';
import useEmbeddedWidgetContext from '../../hooks/use-embedded-widget-context';
import useOpenPanelOnError from '../../hooks/use-open-panel-on-error';

const mapWithKey = uncapFpIterator(map);

type WidgetFormProps = {
  buttonSpacing?: WidgetButtonSpacing;
  data: WidgetData[];
  fluidWidth?: boolean;
  handleCloseWidgetOnTab: (event: KeyboardEvent<HTMLButtonElement>) => void;
  modalStates: boolean[];
  onModalClose: () => void;
  submitButton?: SubmitButtonConfig;
  toggleModal: ToggleModal;
  setIsWidgetActive: SetIsWidgetActive;
  stepInputAppearance?: StepInputAppearance;
  showFieldsRequiredLabel?: boolean;
};

const WidgetForm = <T,>({
  buttonSpacing,
  data,
  fluidWidth,
  handleCloseWidgetOnTab,
  modalStates,
  onModalClose,
  submitButton,
  toggleModal,
  setIsWidgetActive,
  stepInputAppearance,
  showFieldsRequiredLabel,
}: WidgetFormProps) => {
  const hasInputComponent = some((item) => Boolean(item.inputComponent), data);
  const hasInputDataLoaded = some((item) => Boolean(item.inputComponent) && !isEmpty(item.inputData), data);
  const hasInputAndData = hasInputComponent && hasInputDataLoaded;

  const windowObj = browserUtil.getWindow();
  const {
    behavior,
    currentWidgetErrors,
    getCustomParamValues,
    modalFields,
    sessionStorageKeys,
    setCurrentWidgetErrors,
    widgetId,
  } = useEmbeddedWidgetContext();
  const { initialValues, handleSubmit, isSubmitting, resetForm, setSubmitting, values, validateForm, errors } =
    useFormikContext<T>();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hasFormInitialized, setHasFormInitialized] = useState(false);

  useOpenPanelOnError({ modalFields, setIsWidgetActive, toggleModal });
  const router = useRouter();

  const destinationLocation = router.query.productOptionValue as string | undefined;
  const formikSessionStorageKey = `${SESSION_STORAGE_KEYS.FORMIK_STATE}-${widgetId}${destinationLocation ? `-${destinationLocation}` : ''}`;

  const valuesFromUrl = getFormValuesFromUrl<T>({
    getCustomParamValues,
    hasInputComponent,
    hasInputDataLoaded,
    initialValues,
    modalFields,
    queryStringValues: router.query as Partial<T>,
  });

  const rehydrateFormParams = {
    formikSessionStorageKey,
    hasFormInitialized,
    hasInputComponent,
    hasInputDataLoaded,
    resetForm,
    sessionStorageKeys,
    setHasFormInitialized,
    setSubmitting,
    valuesFromUrl,
  };

  const submitButtonText = submitButton?.text ?? 'Get quote';

  useEffect(() => {
    setCurrentWidgetErrors(errors);
  }, [errors, setCurrentWidgetErrors]);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWidgetErrors]);

  // TODO: Consider extracting this form rehydration logic into a custom hook (useFormRehydration) to improve testability and reduce component complexity.
  useEffect(() => {
    const handlePageShow = (event: Event) => {
      const pageTransitionEvent = event as PageTransitionEvent;
      // indicates the page has loaded from a cache (specifically the safari back/forward cache)
      if (pageTransitionEvent.persisted) {
        rehydrateForm<T>({ ...rehydrateFormParams, hasPersistedEventRun: true });
      }
    };
    rehydrateForm<T>({ ...rehydrateFormParams });

    windowObj.addEventListener('pageshow', handlePageShow);

    return () => {
      windowObj.removeEventListener('pageshow', handlePageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInputAndData, valuesFromUrl]);

  useEffect(() => {
    if (hasFormInitialized) {
      if (behavior?.persistFormikState) {
        windowObj.sessionStorage.setItem(formikSessionStorageKey, JSON.stringify(values));
      }

      forEach((sessionStorageKey) => {
        windowObj.sessionStorage.setItem(sessionStorageKey.key, toString(sessionStorageKey.value));
      }, sessionStorageKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [behavior?.persistFormikState, values, sessionStorageKeys, hasInputAndData, hasFormInitialized, widgetId]);

  return (
    <StyledForm>
      <FormWrapper $fluidWidth={fluidWidth}>
        {mapWithKey((field: StepInputProps, index: number) => {
          const isFirst = index === 0;
          const isLast = index === data.length - 1;

          const isActive = modalStates[index] === true;
          const prevPanelHideSeparator = modalStates[index + 1] === true;

          const showInputSeparator = !(
            getShouldHideSeparator(hoveredIndex, index) ||
            isActive ||
            prevPanelHideSeparator
          );

          return (
            <Fragment key={index}>
              <StepInput<T>
                {...field}
                buttonSpacing={buttonSpacing}
                fluidWidth={fluidWidth}
                stepInputAppearance={stepInputAppearance}
                isFirst={isFirst}
                isLast={isLast}
                isModalOpen={modalStates[index]}
                setIsModalOpen={(value) => {
                  toggleModal(index, value);
                  if (value) {
                    setIsWidgetActive(true);
                  }
                }}
                onModalClose={onModalClose}
                index={index}
                showFieldsRequiredLabel={showFieldsRequiredLabel}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
              {!isLast && <InputSeparator $isVisible={showInputSeparator} className="input-separator" />}
            </Fragment>
          );
        }, data)}
        <Hidden xs sm md>
          {/* Desktop submit button layout is controlled via its styled definition. FormWrapper uses max-content width, so be mindful of sizing/overlap when adjusting DesktopSubmitButton positioning in styles. */}
          <DesktopSubmitButton
            icon={{
              name: 'arrow-right-long',
              position: 'right',
              size: 'large',
            }}
            analytics={submitButton?.analyticsLabel ? { label: submitButton.analyticsLabel } : undefined}
            // @ts-expect-error [🤖 PLEASE FIX 🤖]: No overload matches this call.
            onClick={handleSubmit}
            variant="flair"
            onMouseEnter={() => setHoveredIndex(null)}
            onMouseLeave={() => setHoveredIndex(null)}
            isLoading={isSubmitting}
            onKeyDown={handleCloseWidgetOnTab}
          >
            <Hidden lg>{submitButtonText}</Hidden>
          </DesktopSubmitButton>
        </Hidden>
      </FormWrapper>
      {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ children: Element; df: true; xl: true; lg: true; }' is not assignable to type 'IntrinsicAttributes & Pick<HiddenProps, "children"> & Partial<Pick<HiddenProps, "xs" | "sm" | "md" | "lg" | "df" | "isBlock">> & Partial<...>'. */}
      <Hidden df xl lg>
        <MobileSubmitButton
          icon={{
            name: 'arrow-right-long',
            position: 'right',
            size: 'large',
          }}
          analytics={submitButton?.analyticsLabel ? { label: submitButton.analyticsLabel } : undefined}
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: No overload matches this call.
          onClick={handleSubmit}
          type="submit"
          variant="flair"
          isLoading={isSubmitting}
        >
          {submitButtonText}
        </MobileSubmitButton>
      </Hidden>
    </StyledForm>
  );
};

export default WidgetForm;
